import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Phone, MapPin, Clock, Send } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const INITIAL = { name: "", email: "", phone: "", message: "" };

export default function Contact() {
  const [form, setForm] = useState(INITIAL);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Please tell us your name.";
    if (!form.email.trim()) {
      e.email = "We need an email to reply.";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      e.email = "That email doesn't look right.";
    }
    if (!form.message.trim()) e.message = "Don't leave the message empty!";
    return e;
  };

  const onChange = (k) => (ev) => {
    setForm((f) => ({ ...f, [k]: ev.target.value }));
    if (errors[k]) setErrors((er) => ({ ...er, [k]: undefined }));
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    const v = validate();
    if (Object.keys(v).length) {
      setErrors(v);
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/contact`, {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        message: form.message.trim(),
      });
      if (res.data?.ok) {
        toast.success("Message sent. Isaac will be in touch soon.", {
          description: "We typically reply within 24 hours, Mon to Sat.",
        });
        setForm(INITIAL);
      } else {
        toast.error("Something went wrong. Please try again.");
      }
    } catch (err) {
      const detail =
        err?.response?.data?.detail || err?.message || "Could not reach the server.";
      toast.error("Couldn't send the message", { description: detail });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="page-contact" className="bg-white">
      <section className="relative bg-gradient-to-b from-brand-50 to-white">
        <div className="container-page relative pt-16 pb-20 md:pt-24 md:pb-24">
          <div className="max-w-3xl">
            <p className="overline mb-4">Contact</p>
            <h1 className="font-heading h1-fluid font-extrabold text-ink text-balance">
              Say hi to <span className="text-brand">Silky Smooth.</span>
            </h1>
            <p className="mt-6 text-lg text-slate-600 max-w-2xl leading-relaxed">
              Questions, custom quotes, commercial service. Drop us a line.
              Real humans answer, usually within a day.
            </p>
          </div>
        </div>
      </section>

      <section className="section-padding bg-white" data-testid="contact-section">
        <div className="container-page">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
            <div className="lg:col-span-5" data-testid="contact-info">
              <p className="overline mb-3">Reach Us Directly</p>
              <h2 className="font-heading text-3xl sm:text-4xl font-extrabold text-ink mb-8 text-balance">
                We pick up the phone. Or your inbox.
              </h2>

              <ul className="space-y-7">
                <li className="flex items-start gap-4">
                  <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-brand-100 text-brand-700">
                    <Mail size={18} />
                  </span>
                  <div>
                    <div className="text-xs uppercase tracking-[0.18em] text-slate-500 mb-1 font-semibold">
                      Email
                    </div>
                    <a
                      href="mailto:silkysmoothpowerwash@gmail.com"
                      className="font-heading text-lg font-bold text-ink hover:text-brand transition-colors"
                      data-testid="contact-email-link"
                    >
                      silkysmoothpowerwash@gmail.com
                    </a>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-brand-100 text-brand-700">
                    <Phone size={18} />
                  </span>
                  <div>
                    <div className="text-xs uppercase tracking-[0.18em] text-slate-500 mb-1 font-semibold">
                      Phone
                    </div>
                    <a
                      href="tel:+19452722414"
                      className="font-heading text-lg font-bold text-ink hover:text-brand transition-colors"
                      data-testid="contact-phone-link"
                    >
                      (945) 272-2414
                    </a>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-brand-100 text-brand-700">
                    <MapPin size={18} />
                  </span>
                  <div>
                    <div className="text-xs uppercase tracking-[0.18em] text-slate-500 mb-1 font-semibold">
                      Service Area
                    </div>
                    <p className="font-heading text-lg font-bold text-ink leading-snug">
                      Pine Hill and nearby South Jersey communities
                    </p>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-brand-100 text-brand-700">
                    <Clock size={18} />
                  </span>
                  <div>
                    <div className="text-xs uppercase tracking-[0.18em] text-slate-500 mb-1 font-semibold">
                      Hours
                    </div>
                    <p className="font-heading text-lg font-bold text-ink leading-snug">
                      Monday to Saturday · 8 am to 6 pm
                    </p>
                  </div>
                </li>
              </ul>
            </div>

            <div className="lg:col-span-7">
              <form
                onSubmit={onSubmit}
                noValidate
                className="bg-white border border-slate-100 rounded-2xl p-7 md:p-10 shadow-soft"
                data-testid="contact-form"
              >
                <h3 className="font-heading text-2xl sm:text-3xl font-extrabold text-ink mb-2">
                  Send us a message
                </h3>
                <p className="text-sm text-slate-500 mb-7">
                  We'll never share your info. Pinky promise.
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <Field
                    label="Name"
                    id="name"
                    placeholder="Your full name"
                    value={form.name}
                    onChange={onChange("name")}
                    error={errors.name}
                    testId="contact-input-name"
                    required
                  />
                  <Field
                    label="Email"
                    id="email"
                    type="email"
                    placeholder="you@example.com"
                    value={form.email}
                    onChange={onChange("email")}
                    error={errors.email}
                    testId="contact-input-email"
                    required
                  />
                </div>
                <div className="mt-5">
                  <Field
                    label="Phone (optional)"
                    id="phone"
                    type="tel"
                    placeholder="(856) 555-0000"
                    value={form.phone}
                    onChange={onChange("phone")}
                    testId="contact-input-phone"
                  />
                </div>
                <div className="mt-5">
                  <label
                    htmlFor="message"
                    className="block text-xs font-semibold uppercase tracking-[0.18em] text-slate-500 mb-2"
                  >
                    Message <span className="text-brand">*</span>
                  </label>
                  <textarea
                    id="message"
                    rows={6}
                    value={form.message}
                    onChange={onChange("message")}
                    placeholder="Tell us about your bins, your home, anything we should know…"
                    className={`field-input min-h-[140px] sm:min-h-[160px] ${errors.message ? "border-red-400" : ""}`}
                    data-testid="contact-input-message"
                  />
                  {errors.message && (
                    <p className="mt-2 text-xs text-red-500">{errors.message}</p>
                  )}
                </div>

                <div className="mt-7 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <p className="text-xs text-slate-400">
                    We typically reply within 24 hours.
                  </p>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="btn-primary w-full sm:w-auto"
                    data-testid="contact-submit-button"
                  >
                    {submitting ? "Sending…" : "Send Message"}
                    {!submitting && <Send size={14} />}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function Field({ label, id, type = "text", placeholder, value, onChange, error, testId, required }) {
  return (
    <div>
      <label
        htmlFor={id}
        className="block text-xs font-semibold uppercase tracking-[0.18em] text-slate-500 mb-2"
      >
        {label} {required && <span className="text-brand">*</span>}
      </label>
      <input
        id={id}
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className={`field-input ${error ? "border-red-400" : ""}`}
        data-testid={testId}
      />
      {error && <p className="mt-2 text-xs text-red-500">{error}</p>}
    </div>
  );
}
